import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import UserManagement from '../components/UserManagement';
import ProductManagement from '../components/ProductManagement';
import KeysManagement from '../components/KeysManagement';
import DirectPurchase from '../components/DirectPurchase';
import Generate from '../components/Generate';
import SystemSettings from '../components/SystemSettings';
import axios from 'axios';

const Admin = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalKeys: 0,
    totalProducts: 0,
    totalRevenue: 0,
    usersByRole: {},
    keysByStatus: {},
    recentActivity: { users: [], keys: [] }
  });

  useEffect(() => {
    if (activeTab === 'overview') {
      fetchAdminStats();
    }
  }, [activeTab]);

  const fetchAdminStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch admin stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    ...(user?.role === "reseller" ? [{ id: 'overview', name: 'Overview', icon: '📊' },
      { id: 'keys', name: 'License Keys', icon: '🔑' },
      { id: 'direct-purchase', name: 'Direct Purchase', icon: '💳' },
    ] : []),

    ...(user?.role === 'owner' ? [{ id: 'overview', name: 'Overview', icon: '📊' },
    { id: 'users', name: 'Users', icon: '👥' },
    { id: 'products', name: 'Products', icon: '📦' },
    { id: 'keys', name: 'License Keys', icon: '🔑' },
    { id: 'generate', name: 'Generate Keys', icon: '⚡' },
    { id: 'direct-purchase', name: 'Direct Purchase', icon: '💳' },
    { id: 'settings', name: 'Settings', icon: '⚙️' }] : [])
  ];

  const StatCard = ({ title, value, icon, color = 'purple', subtitle = '' }) => (
    <div className={`card-astral p-6 hover:glow-${color} transition-all duration-300`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-400 text-sm font-medium">{title}</p>
          <p className="text-2xl font-bold text-white mt-1">{value}</p>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className="text-3xl opacity-80">{icon}</div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="spinner"></div>
              </div>
            ) : (
              <>
                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <StatCard
                    title="Total Users"
                    value={stats.totalUsers}
                    icon="👥"
                    color="blue"
                    subtitle={`Active: ${stats.usersByRole?.user || 0}`}
                  />
                  <StatCard
                    title="Total Products"
                    value={stats.totalProducts}
                    icon="📦"
                    color="green"
                  />
                  <StatCard
                    title="License Keys"
                    value={stats.totalKeys}
                    icon="🔑"
                    color="purple"
                    subtitle={`Available: ${stats.keysByStatus?.available || 0}`}
                  />
                  <StatCard
                    title="Revenue"
                    value={`${stats.totalRevenue || 0} credits`}
                    icon="💰"
                    color="yellow"
                  />
                </div>

                {/* Charts and Analytics */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* User Roles Distribution */}
                  <div className="card-astral p-6">
                    <h3 className="text-lg font-bold text-white mb-4">User Distribution</h3>
                    <div className="space-y-3">
                      {Object.entries(stats.usersByRole || {}).map(([role, count]) => (
                        <div key={role} className="flex justify-between items-center">
                          <span className="text-gray-400 capitalize">{role}s</span>
                          <div className="flex items-center gap-2">
                            <div className={`w-16 h-2 bg-gray-700 rounded-full overflow-hidden`}>
                              <div 
                                className={`h-full ${
                                  role === 'owner' ? 'bg-red-500' : 
                                  role === 'reseller' ? 'bg-yellow-500' : 'bg-green-500'
                                }`}
                                style={{
                                  width: `${(count / stats.totalUsers) * 100}%`
                                }}
                              />
                            </div>
                            <span className="text-white font-semibold w-8 text-right">{count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* License Key Status */}
                  <div className="card-astral p-6">
                    <h3 className="text-lg font-bold text-white mb-4">License Key Status</h3>
                    <div className="space-y-3">
                      {Object.entries(stats.keysByStatus || {}).map(([status, count]) => (
                        <div key={status} className="flex justify-between items-center">
                          <span className="text-gray-400 capitalize">{status}</span>
                          <div className="flex items-center gap-2">
                            <div className={`w-16 h-2 bg-gray-700 rounded-full overflow-hidden`}>
                              <div 
                                className={`h-full ${
                                  status === 'available' ? 'bg-green-500' : 
                                  status === 'used' ? 'bg-blue-500' : 
                                  status === 'revoked' ? 'bg-red-500' : 'bg-yellow-500'
                                }`}
                                style={{
                                  width: `${(count / stats.totalKeys) * 100}%`
                                }}
                              />
                            </div>
                            <span className="text-white font-semibold w-8 text-right">{count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Recent Users */}
                  {user?.role === 'owner' && stats.recentActivity?.users?.length > 0 && (
                    <div className="card-astral p-6">
                      <h3 className="text-lg font-bold text-white mb-4">Recent Users</h3>
                      <div className="space-y-3">
                        {stats.recentActivity.users.map((recentUser) => (
                          <div key={recentUser._id} className="flex justify-between items-center bg-dark-200 p-3 rounded">
                            <div>
                              <div className="text-white font-semibold">{recentUser.username}</div>
                              <div className="text-gray-400 text-sm">{recentUser.email}</div>
                            </div>
                            <div className="text-right">
                              <div className={`px-2 py-1 text-xs rounded-full capitalize ${
                                recentUser.role === 'owner' ? 'text-red-400 bg-red-600/20' :
                                recentUser.role === 'reseller' ? 'text-yellow-400 bg-yellow-600/20' :
                                'text-green-400 bg-green-600/20'
                              }`}>
                                {recentUser.role}
                              </div>
                              <div className="text-gray-500 text-xs mt-1">
                                {new Date(recentUser.createdAt).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recent Keys */}
                  {stats.recentActivity?.keys?.length > 0 && (
                    <div className="card-astral p-6">
                      <h3 className="text-lg font-bold text-white mb-4">Recent License Keys</h3>
                      <div className="space-y-3">
                        {stats.recentActivity.keys.map((key) => (
                          <div key={key._id} className="flex justify-between items-center bg-dark-200 p-3 rounded">
                            <div>
                              <div className="text-white font-semibold">{key.product?.name || 'Unknown Product'}</div>
                              <code className="text-purple-400 text-sm font-mono">{key.key}</code>
                            </div>
                            <div className="text-right">
                              <div className={`px-2 py-1 text-xs rounded-full capitalize ${
                                key.status === 'available' ? 'text-green-400 bg-green-600/20' :
                                key.status === 'used' ? 'text-blue-400 bg-blue-600/20' :
                                key.status === 'revoked' ? 'text-red-400 bg-red-600/20' :
                                'text-yellow-400 bg-yellow-600/20'
                              }`}>
                                {key.status}
                              </div>
                              <div className="text-gray-500 text-xs mt-1">
                                {new Date(key.createdAt).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="card-astral p-6">
                  <h3 className="text-lg font-bold text-white mb-4">Quick Actions</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {user?.role === 'owner' && (
                      <>
                        <button
                          onClick={() => setActiveTab('users')}
                          className="btn-neon text-sm py-3"
                        >
                          👥 Manage Users
                        </button>
                        <button
                          onClick={() => setActiveTab('products')}
                          className="btn-neon text-sm py-3"
                        >
                          📦 Manage Products
                        </button>
                        <button
                          onClick={() => setActiveTab('generate')}
                          className="btn-neon text-sm py-3"
                        >
                          🔑 Generate Keys
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => setActiveTab('keys')}
                      className="btn-neon-blue text-sm py-3"
                    >
                      🗝️ View All Keys
                    </button>
                  </div>
                </div>

                {/* System Info */}
                <div className="card-astral p-6">
                  <h3 className="text-lg font-bold text-white mb-4">System Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                    <div className="bg-dark-200 p-3 rounded">
                      <div className="text-gray-400">User Role</div>
                      <div className="text-white font-semibold capitalize">{user?.role}</div>
                    </div>
                    <div className="bg-dark-200 p-3 rounded">
                      <div className="text-gray-400">Total Credits</div>
                      <div className="text-green-400 font-semibold">{user?.credits || 0}</div>
                    </div>
                    <div className="bg-dark-200 p-3 rounded">
                      <div className="text-gray-400">USD Balance</div>
                      <div className="text-blue-400 font-semibold">${user?.balance || 0}</div>
                    </div>
                    <div className="bg-dark-200 p-3 rounded">
                      <div className="text-gray-400">Last Login</div>
                      <div className="text-white font-semibold">
                        {user?.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        );
      case 'users':
        return <UserManagement />;
      case 'products':
        return <ProductManagement />;
      case 'keys':
        return <KeysManagement />;
      case 'generate':
        return <Generate />;
      case 'direct-purchase':
        return <DirectPurchase />;
      case 'settings':
        return user?.role === 'owner' ? <SystemSettings /> : <div>Access denied</div>;
      default:
        return <div>Select a tab</div>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="card-astral p-6">
        <h1 className="text-3xl font-bold text-white mb-2">Admin Panel</h1>
        <p className="text-gray-400">
          Manage your license platform and oversee all operations.
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="card-astral p-1">
        <div className="flex space-x-1 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center px-4 py-2 rounded-lg transition-all duration-200 whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-400 hover:text-purple-400 hover:bg-purple-600/10'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.name}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {renderTabContent()}
    </div>
  );
};

export default Admin;
